/* eslint-disable jsx-a11y/no-redundant-roles -- role='list' / 'listitem' are the design's: Safari drops list semantics from a list styled with list-style: none */
import { Trans, useTranslation } from 'next-i18next';
import type { CSSProperties, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';

import { PauseChip } from '../components/pause-chip';
import { TrialCta } from '../components/trial-cta';
import { CONTAINER_CLASS, EYEBROW_CLASS, GOLD_EM_CLASS, SECTION_CLASS, SECTION_HEAD_CENTER_CLASS, SECTION_LEAD_CENTER_CLASS, SECTION_TITLE_CLASS, SectionBackground } from '../components/ui';
import { FINAL_FORM_ANCHOR, V4A_NAMESPACE } from '../constants';
import { useDocumentVisible, useOnScreen, useReducedMotion } from '../hooks/use-page-motion';

const varStyle = (values: Record<string, string>) => values as CSSProperties;

const BREW_CLASS = 'brew relative grid min-w-0 grid-rows-[auto_auto_1fr_auto] gap-4 overflow-hidden p-[clamp(1.15rem,_.8rem_+_1.2vw,_1.9rem)] [box-shadow:var(--shadow-md),_0_0_0_1px_rgb(var(--gold-rgb)_/_.05)_inset]';
const BREW_HEAD_CLASS = 'brew__head relative grid min-w-0 grid-cols-[auto_minmax(0,_1fr)] items-center gap-x-4 gap-y-3 min-[544px]:grid-cols-[auto_minmax(0,_1fr)_auto]';
const BREW_ICON_CLASS = 'brew__ico inline-flex h-[3.25rem] w-[3.25rem] items-center justify-center bg-[color:rgb(var(--gold-rgb)_/_.11)] text-[color:var(--gold)]';
const BREW_TITLE_CLASS = 'brew__t m-0 text-[length:clamp(1.2rem,_1.02rem_+_.55vw,_1.55rem)] font-bold leading-[1.15] text-[color:var(--fg-strong)]';
const BREW_KICKER_CLASS = 'brew__k m-0 mt-[.3rem] font-mono text-[length:var(--fs-2xs)] font-medium uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--accent)]';
const BREW_CHIP_CLASS = 'brew__chip col-span-full inline-flex items-center justify-self-start rounded-full border border-solid border-[color:rgb(var(--gold-rgb)_/_.34)] bg-[color:rgb(var(--gold-rgb)_/_.1)] px-[.68rem] py-[.34rem] font-mono text-[length:var(--fs-2xs)] font-medium uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--gold-hi)] min-[544px]:col-[auto] min-[544px]:justify-self-end';
const BREW_DESCRIPTION_CLASS = 'brew__d relative m-0 min-w-0 max-w-[40em] text-[length:var(--fs-sm)] leading-[1.62] text-[color:var(--fg-dim)]';
const BREW_FOOT_CLASS = 'brew__foot relative m-0 flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 text-[length:var(--fs-2xs)] leading-[1.5] text-[color:var(--fg-faint-aa)]';
const FPS_ROW_CLASS = 'fps__row grid grid-cols-[minmax(0,_1fr)_auto] items-center gap-x-3 gap-y-[.45rem] min-[400.16px]:grid-cols-[minmax(0,_7.5em)_minmax(0,_1fr)_auto] min-[400.16px]:gap-y-2';
const FPS_LABEL_CLASS = 'fps__lab m-0 font-mono text-[length:var(--fs-2xs)] uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--fg-dim)]';
const FPS_BAR_CLASS = 'fps__bar relative h-4 overflow-hidden rounded-full forced-colors:border forced-colors:border-solid forced-colors:border-[color:CanvasText]';
const FPS_FILL_CLASS = 'fps__fill absolute inset-y-0 end-auto start-0 rounded-[inherit] forced-colors:shadow-none';
const FPS_VALUE_CLASS = 'tabular inline-block min-w-[3ch] whitespace-nowrap text-end text-[length:clamp(1.15rem,_1rem_+_.5vw,_1.45rem)] font-bold tabular-nums leading-none';
const FPS_UNIT_CLASS = 'font-mono text-[length:var(--fs-2xs)] uppercase not-italic tracking-[var(--ls-eyebrow)] text-[color:var(--fg-dim)]';

/* ---------------------------------------------------------------- FPS potion */
const REF_BAD = 86;
const REF_GOOD = 180;
const SCALE = 184;
const CENTER = 90.8;

/** A live, unstable "sem a poção" reading against a high, steady "com a poção"; the gauge accelerates. */
function FpsPotion() {
  const { t } = useTranslation(V4A_NAMESPACE);
  const reduced = useReducedMotion();
  const pageVisible = useDocumentVisible();
  const brewRef = useRef<HTMLElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const badValueRef = useRef<HTMLElement>(null);
  const goodValueRef = useRef<HTMLElement>(null);
  const badFillRef = useRef<HTMLSpanElement>(null);
  const goodFillRef = useRef<HTMLSpanElement>(null);
  const deltaRef = useRef<HTMLSpanElement>(null);
  const onScreen = useOnScreen(brewRef, { threshold: 0.25 });
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    // the gauge (CSS) restarts from LOW on each arrival
    brewRef.current?.classList.toggle('is-live', onScreen && !reduced);
  }, [onScreen, reduced]);
  useEffect(() => {
    brewRef.current?.classList.toggle('is-paused', paused);
  }, [paused]);

  useEffect(() => {
    const box = boxRef.current;
    const badFill = badFillRef.current;
    const goodFill = goodFillRef.current;
    if (!box || !badFill || !goodFill) return undefined;
    const paint = (bad: number, good: number) => {
      if (badValueRef.current) badValueRef.current.textContent = String(bad);
      if (goodValueRef.current) goodValueRef.current.textContent = String(good);
      if (deltaRef.current) deltaRef.current.textContent = String(good - bad);
      badFill.style.setProperty('--w', `${((bad / SCALE) * 100).toFixed(1)}%`);
      goodFill.style.setProperty('--w', `${((good / SCALE) * 100).toFixed(1)}%`);
    };
    const reset = () => {
      box.classList.remove('is-live');
      badFill.classList.remove('is-dip');
      paint(REF_BAD, REF_GOOD);
    };
    if (reduced || !onScreen || paused || !pageVisible) {
      reset();
      return undefined;
    }
    let current = REF_BAD;
    let sum = 0;
    let count = 0;
    let interval = 0;
    /* without the potion: a mean-reverting wobble around 86 with stutters; the pull target sits above 86
       to pay back the downward stutters, so the running mean lands on 86 +- 2 */
    const step = () => {
      const mean = count ? sum / count : REF_BAD;
      const target = CENTER + 1.6 * (REF_BAD - mean);
      let bad = current + (target - current) * 0.35 + (Math.random() - 0.5) * 14;
      const dip = Math.random() < 0.13;
      if (dip) bad -= 9 + Math.random() * 11;
      bad = Math.round(Math.max(66, Math.min(102, bad)));
      current = bad;
      sum += bad;
      count += 1;
      badFill.classList.toggle('is-dip', dip);
      paint(bad, Math.round(REF_GOOD + (Math.random() - 0.5) * 6));
    };
    // let the reveal grow the bars first, then start the live readout
    const wait = window.setTimeout(() => {
      box.classList.add('is-live');
      step();
      interval = window.setInterval(step, 260);
    }, 900);
    return () => {
      window.clearTimeout(wait);
      window.clearInterval(interval);
      reset();
    };
  }, [reduced, onScreen, paused, pageVisible]);

  return (
    <article ref={brewRef} className={`${BREW_CLASS} brew--fps`}>
      <header className={BREW_HEAD_CLASS}>
        <span className={BREW_ICON_CLASS} aria-hidden='true'>
          <svg className='gauge' viewBox='0 0 24 24' width='26' height='26' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' focusable='false'>
            <path className='gauge__track' d='M4.5 17.5a8.5 8.5 0 1 1 15 0' />
            <path className='gauge__fill' pathLength={1} d='M4.5 17.5a8.5 8.5 0 1 1 15 0' />
            <path className='gauge__needle' d='m12 13.5 4.2-4.4' />
            <circle cx='12' cy='13.5' r='1.4' fill='currentColor' />
          </svg>
        </span>
        <div className='brew__id min-w-0'>
          <h3 className={BREW_TITLE_CLASS}>{t('potions.fps.title')}</h3>
          <p className={BREW_KICKER_CLASS}>FPS Boost</p>
        </div>
        <span className={BREW_CHIP_CLASS}>{t('potions.fps.chip')}</span>
      </header>
      <p className={BREW_DESCRIPTION_CLASS}>{t('potions.fps.description')}</p>
      <div ref={boxRef} className='fps relative grid min-w-0 content-center gap-[clamp(.8rem,_.6rem_+_.6vw,_1.15rem)] rounded-[var(--r-md)] border border-solid border-[color:var(--rim-2)] p-[clamp(.9rem,_.7rem_+_.6vw,_1.25rem)]' data-fps='' role='img' aria-label={t('potions.fps.aria')}>
        <p className='fps__cap m-0 flex flex-wrap justify-between gap-x-3 gap-y-[.2rem] border-b border-solid border-b-[color:var(--rim-2)] pb-2 font-mono text-[length:var(--fs-2xs)] uppercase tracking-[var(--ls-eyebrow)]'>
          <span className='text-[color:var(--gold)]'>{t('potions.fps.caption')}</span>
          <span className='text-[color:var(--fg-faint-aa)]'>{t('potions.fps.caption_note')}</span>
        </p>
        <div className={FPS_ROW_CLASS}>
          <p className={FPS_LABEL_CLASS}>{t('potions.fps.without')}</p>
          <div className={FPS_BAR_CLASS}>
            <span ref={badFillRef} className={`${FPS_FILL_CLASS} fps__fill--bad`} style={varStyle({ '--w': '48%' })} />
          </div>
          <p className='fps__val m-0 inline-flex items-baseline gap-[.2rem] text-[color:var(--fg-muted)]'>
            <b ref={badValueRef} className={`${FPS_VALUE_CLASS} text-[color:var(--fg-strong)]`} data-fps-bad=''>
              86
            </b>
            <em className={FPS_UNIT_CLASS}>fps</em>
          </p>
        </div>
        <div className={FPS_ROW_CLASS}>
          <p className={FPS_LABEL_CLASS}>{t('potions.fps.with')}</p>
          <div className={FPS_BAR_CLASS}>
            <span ref={goodFillRef} className={`${FPS_FILL_CLASS} fps__fill--good [box-shadow:0_0_18px_rgb(var(--accent-rgb)_/_.35)]`} style={varStyle({ '--w': '100%' })} />
          </div>
          <p className='fps__val fps__val--good m-0 inline-flex items-baseline gap-[.2rem] text-[color:var(--fg-muted)]'>
            <b ref={goodValueRef} className={`${FPS_VALUE_CLASS} text-[color:var(--accent)] [text-shadow:0_0_18px_rgb(var(--accent-rgb)_/_.35)]`} data-fps-good=''>
              180
            </b>
            <em className={FPS_UNIT_CLASS}>fps</em>
          </p>
        </div>
        <p className='fps__delta m-0 flex flex-wrap items-center gap-x-3 gap-y-[.45rem] border-t border-solid border-t-[color:var(--rim-2)] pt-2 text-[length:var(--fs-2xs)] text-[color:var(--fg-dim)]'>
          <span className='fps__chip inline-flex min-w-[9.6em] items-center justify-center rounded-full bg-[color:var(--accent)] px-[.55rem] py-1 font-mono text-[length:var(--fs-2xs)] font-medium uppercase tabular-nums tracking-[var(--ls-eyebrow)] text-[color:var(--accent-ink,_#0B1400)]'>
            <Trans t={t} i18nKey='potions.fps.delta' components={{ n: <span ref={deltaRef} data-fps-delta='' /> }} />
          </span>
          {t('potions.fps.delta_note')}
        </p>
      </div>
      <div className={BREW_FOOT_CLASS}>
        <p className='m-0 min-w-0 flex-[1_1_14rem]'>{t('potions.fps.foot')}</p>
        <PauseChip paused={paused} onToggle={() => setPaused((previous) => !previous)} labelPause={t('potions.fps.pause')} labelResume={t('potions.fps.resume')} hidden={reduced} />
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ AudioPad */
const PAD_KEYS = ['Q', 'W', 'E', 'A', 'S', 'D', 'Z', 'X', 'C'] as const;
const PAD_HOT = 4;
/* vamo, buzina, gg, buzina, risada... (key indexes) */
const SEQUENCE = [0, 4, 2, 4, 3, 5, 1, 4, 6, 8, 7, 4];
const HIT = 1100;
const DECAY = 700;
const DEFAULT_BARS = 36;

function restHeight(index: number): number {
  return 0.2 + 0.62 * Math.abs(Math.sin(index * 0.37) * Math.sin(index * 0.11 + 0.6));
}

function grain(seed: number): number {
  const value = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return value - Math.floor(value);
}

/** The keys play in sequence; the deck shows the sample and a waveform that spikes on every hit. */
function AudioPad() {
  const { t } = useTranslation(V4A_NAMESPACE);
  const reduced = useReducedMotion();
  const pageVisible = useDocumentVisible();
  const brewRef = useRef<HTMLElement>(null);
  const padRef = useRef<HTMLUListElement>(null);
  const eqRef = useRef<HTMLSpanElement>(null);
  const nowKeyRef = useRef<HTMLElement>(null);
  const nowNameRef = useRef<HTMLSpanElement>(null);
  const onScreen = useOnScreen(brewRef, { threshold: 0.25 });
  const [paused, setPaused] = useState(false);
  const [barCount, setBarCount] = useState(DEFAULT_BARS);
  const labels = PAD_KEYS.map((_key, index) => t(`potions.pad.keys.${index}`));

  // the waveform always fills its strip: one bar per ~5 px (3 px bar + 2 px gap)
  useEffect(() => {
    const eq = eqRef.current;
    if (!eq) return undefined;
    let timer = 0;
    const measure = () => setBarCount(Math.max(6, Math.floor((eq.clientWidth + 2) / 5)));
    measure();
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, 150);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    brewRef.current?.classList.toggle('is-paused', paused);
  }, [paused]);

  useEffect(() => {
    const pad = padRef.current;
    const eq = eqRef.current;
    if (!pad || !eq) return undefined;
    pad.style.setProperty('--hit', `${HIT}ms`);
    const keys = Array.from(pad.querySelectorAll<HTMLElement>('.pad__key'));
    const bars = Array.from(eq.children) as HTMLElement[];
    const restKey = PAD_KEYS[PAD_HOT] ?? 'S';
    const restName = labels[PAD_HOT] ?? '';
    const stop = (leaving: boolean) => {
      keys.forEach((key) => key.classList.remove('is-playing'));
      bars.forEach((bar, index) => bar.style.setProperty('--h', restHeight(index).toFixed(2)));
      if (leaving) {
        pad.classList.remove('is-seq');
        if (nowKeyRef.current) nowKeyRef.current.textContent = restKey;
        if (nowNameRef.current) nowNameRef.current.textContent = restName;
      }
    };
    if (reduced || !onScreen || paused || !pageVisible) {
      stop(!onScreen);
      return undefined;
    }
    let position = 0;
    let lastHit = -1e9;
    let startedAt = 0;
    let hitTimer = 0;
    let eqTimer = 0;
    let offTimer = 0;
    const hit = () => {
      const keyIndex = SEQUENCE[position % SEQUENCE.length] ?? PAD_HOT;
      position += 1;
      const key = keys[keyIndex];
      if (!key) return;
      keys.forEach((other) => other.classList.remove('is-playing'));
      key.getBoundingClientRect(); // restart the press on a repeated key
      key.classList.add('is-playing');
      window.clearTimeout(offTimer);
      offTimer = window.setTimeout(() => key.classList.remove('is-playing'), HIT - 30);
      if (nowKeyRef.current) nowKeyRef.current.textContent = PAD_KEYS[keyIndex] ?? '';
      if (nowNameRef.current) nowNameRef.current.textContent = labels[keyIndex] ?? '';
      lastHit = performance.now();
    };
    /* a mirrored waveform that keeps breathing (two slow sines + grain) and spikes at its newest edge */
    const equalizer = () => {
      const now = performance.now();
      const time = (now - startedAt) / 1000;
      const count = bars.length;
      const peak = Math.max(0, 1 - (now - lastHit) / DECAY);
      bars.forEach((bar, index) => {
        let value = 0.18 + 0.82 * Math.abs(Math.sin(time * 2.3 + index * 0.13) * Math.sin(time * 0.9 + index * 0.052));
        value *= 0.75 + grain(index + Math.floor(time * 18)) * 0.25;
        if (index > count - 11) value = Math.max(value, peak * (1 - (count - 1 - index) / 11));
        bar.style.setProperty('--h', Math.max(0.06, Math.min(1, value)).toFixed(3));
      });
    };
    const wait = window.setTimeout(() => {
      startedAt = performance.now();
      pad.classList.add('is-seq');
      hit();
      hitTimer = window.setInterval(hit, HIT);
      eqTimer = window.setInterval(equalizer, 70);
    }, 700);
    return () => {
      window.clearTimeout(wait);
      window.clearTimeout(offTimer);
      window.clearInterval(hitTimer);
      window.clearInterval(eqTimer);
      stop(false);
    };
    // `labels` changes identity every render; the texts only change with the locale, which remounts the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced, onScreen, paused, pageVisible, barCount]);

  const bars = Array.from({ length: barCount }, (_bar, index) => ({ index, height: restHeight(index).toFixed(2), opacity: (0.3 + (0.7 * index) / Math.max(1, barCount - 1)).toFixed(2) }));

  return (
    <article ref={brewRef} className={`${BREW_CLASS} brew--pad`}>
      <header className={BREW_HEAD_CLASS}>
        <span className={BREW_ICON_CLASS} aria-hidden='true'>
          <svg viewBox='0 0 24 24' width='26' height='26' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinejoin='round' focusable='false'>
            <rect x='3.2' y='3.2' width='7.4' height='7.4' rx='1.6' />
            <rect x='13.4' y='3.2' width='7.4' height='7.4' rx='1.6' />
            <rect x='3.2' y='13.4' width='7.4' height='7.4' rx='1.6' />
            <rect x='13.4' y='13.4' width='7.4' height='7.4' rx='1.6' fill='currentColor' stroke='none' opacity='.9' />
          </svg>
        </span>
        <div className='brew__id min-w-0'>
          <h3 className={BREW_TITLE_CLASS}>{t('potions.pad.title')}</h3>
          <p className={BREW_KICKER_CLASS}>AudioPad</p>
        </div>
        <span className={BREW_CHIP_CLASS}>{t('potions.pad.chip')}</span>
      </header>
      <p className={BREW_DESCRIPTION_CLASS}>{t('potions.pad.description')}</p>
      <div className='pad__stage relative grid min-w-0 grid-rows-[minmax(0,_1fr)_auto] gap-3'>
        <ul ref={padRef} className='pad m-0 grid list-none grid-cols-3 grid-rows-[repeat(3,_minmax(3.4rem,_1fr))] gap-[.45rem] p-0' role='list' aria-label={t('potions.pad.keys_label')}>
          {PAD_KEYS.map((key, index) => (
            <li key={key} className={index === PAD_HOT ? 'pad__key pad__key--hot relative grid min-h-[3.4rem] min-w-0 content-center gap-[.3rem] overflow-hidden px-[.55rem] py-2' : 'pad__key relative grid min-h-[3.4rem] min-w-0 content-center gap-[.3rem] overflow-hidden px-[.55rem] py-2 [box-shadow:inset_0_1px_0_rgb(255_255_255_/_.07),_0_1px_0_rgb(0_0_0_/_.4)]'} style={varStyle({ '--k': String(index) })}>
              <b className='font-mono text-[length:var(--fs-2xs)] font-bold leading-none tracking-[var(--ls-eyebrow)]' aria-hidden='true'>
                {key}
              </b>
              <span className='text-[length:var(--fs-xs)] leading-[1.2] [overflow-wrap:anywhere]'>{labels[index]}</span>
            </li>
          ))}
        </ul>
        <div className='pad__deck relative flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 overflow-hidden rounded-[var(--r-sm)] border border-solid border-[color:var(--rim-2)] px-3 py-2' aria-hidden='true'>
          <span className='pad__hud grid min-w-[7.75rem] flex-none gap-[.3rem]'>
            <span className='pad__lab font-mono text-[length:11px] uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--fg-faint-aa)]'>Soundboard</span>
            <span className='pad__now inline-flex items-center gap-[.4rem] justify-self-start whitespace-nowrap rounded-full border border-solid border-[color:rgb(var(--accent-rgb)_/_.45)] bg-[color:rgb(8_6_15_/_.7)] px-2 py-[.22rem] font-mono text-[length:var(--fs-2xs)] uppercase tracking-[.08em] text-[color:var(--fg)]'>
              <i className='size-[6px] flex-none rounded-[50%] bg-[color:var(--accent)] [box-shadow:0_0_8px_var(--accent)]' />
              <b ref={nowKeyRef} className='font-bold text-[color:var(--accent)]' data-pad-key=''>
                {PAD_KEYS[PAD_HOT]}
              </b>
              <span ref={nowNameRef} data-pad-name=''>
                {labels[PAD_HOT]}
              </span>
            </span>
          </span>
          <span ref={eqRef} className='pad__eq flex h-10 min-w-0 flex-[1_1_8rem] items-center gap-[2px] overflow-hidden' data-pad-eq=''>
            {bars.map((bar) => (
              <i key={bar.index} className='h-full min-w-0 flex-1 rounded-[2px] bg-[color:var(--accent)] [box-shadow:0_0_6px_rgb(var(--accent-rgb)_/_.55)]' style={varStyle({ '--h': bar.height, '--o': bar.opacity })} />
            ))}
          </span>
        </div>
      </div>
      <div className={BREW_FOOT_CLASS}>
        <p className='m-0 min-w-0 flex-[1_1_14rem]'>{t('potions.pad.foot')}</p>
        <PauseChip paused={paused} onToggle={() => setPaused((previous) => !previous)} labelPause={t('potions.pad.pause')} labelResume={t('potions.pad.resume')} hidden={reduced} />
      </div>
    </article>
  );
}

interface Vial {
  key: 'aim' | 'replay' | 'settings' | 'multi';
  product: string;
  icon: ReactElement;
}

const VIALS: readonly Vial[] = [
  {
    key: 'aim',
    product: 'Aim Trainer',
    icon: (
      <>
        <circle cx='12' cy='12' r='7.5' />
        <circle cx='12' cy='12' r='2.2' />
        <path d='M12 1.8v3.2M12 19v3.2M1.8 12H5M19 12h3.2' />
      </>
    ),
  },
  {
    key: 'replay',
    product: 'Replay X',
    icon: (
      <>
        <path d='M3.8 12a8.2 8.2 0 1 0 2.6-6' />
        <path d='M3.4 3.6v4.6h4.6' />
        <path d='m10.6 9.4 4.6 2.6-4.6 2.6z' />
      </>
    ),
  },
  {
    key: 'settings',
    product: 'Pro Settings',
    icon: (
      <>
        <path d='M4 5.2c2.6-1.1 5.3-1.1 8 0v13.6c-2.7-1.1-5.4-1.1-8 0z' />
        <path d='M12 5.2c2.7-1.1 5.4-1.1 8 0v13.6c-2.6-1.1-5.3-1.1-8 0' />
        <path d='M12 5.2v13.6' />
      </>
    ),
  },
  {
    key: 'multi',
    product: 'Multi Internet',
    icon: (
      <>
        <path d='M12 2.8 4.8 5.6v5.1c0 4.4 3 8.3 7.2 9.3 4.2-1 7.2-4.9 7.2-9.3V5.6z' />
        <path d='M8.6 12.4a3.6 3.6 0 0 0 6.8 0' />
        <path d='M12 6.6v3.2' />
      </>
    ),
  },
];

/** "O laboratório de poções": the six tools, two of them live. */
export function Potions() {
  const { t } = useTranslation(V4A_NAMESPACE);
  return (
    <section className={`${SECTION_CLASS} sec--potions [--reveal-stagger:110ms]`} id='pocoes' aria-labelledby='pot-title'>
      <SectionBackground
        halos={[
          { position: 'left', tone: 'gold' },
          { position: 'right', tone: 'violet' },
        ]}
      />
      <div className={CONTAINER_CLASS}>
        <div className={SECTION_HEAD_CENTER_CLASS} data-reveal=''>
          <p className={`${EYEBROW_CLASS} min-w-0`}>{t('potions.eyebrow')}</p>
          <h2 className={`${SECTION_TITLE_CLASS} min-w-0`} id='pot-title'>
            <Trans t={t} i18nKey='potions.title' components={{ em: <em className={GOLD_EM_CLASS} /> }} />
          </h2>
          <p className={SECTION_LEAD_CENTER_CLASS}>{t('potions.lead')}</p>
        </div>

        <div className='lab grid gap-[clamp(1rem,_.6rem_+_1.2vw,_1.5rem)] min-[992px]:grid-cols-2' data-reveal-group=''>
          <FpsPotion />
          <AudioPad />
          <ul className='vials m-0 grid list-none gap-[clamp(1rem,_.6rem_+_1.2vw,_1.5rem)] p-0 sm:grid-cols-2 min-[992px]:col-span-full min-[992px]:grid-cols-4' role='list' data-reveal-group=''>
            {VIALS.map((vial) => (
              <li key={vial.key} className='vial relative grid min-w-0 content-start gap-3 p-[clamp(1rem,_.8rem_+_.7vw,_1.35rem)] [box-shadow:var(--shadow-sm,_0_8px_22px_rgb(0_0_0_/_.35))]'>
                <span className='vial__ico inline-flex size-10 min-w-0 items-center justify-center bg-[color:rgb(var(--gold-rgb)_/_.08)]' aria-hidden='true'>
                  <svg viewBox='0 0 24 24' width='22' height='22' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' focusable='false'>
                    {vial.icon}
                  </svg>
                </span>
                <h3 className='vial__t m-0 min-w-0 text-[length:var(--fs-lg)] font-bold leading-[1.22] text-[color:var(--fg-strong)]'>
                  {t(`potions.vials.${vial.key}.title`)} <span className='vial__k mt-[.28rem] block font-mono text-[length:var(--fs-2xs)] font-medium uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--accent)]'>{vial.product}</span>
                </h3>
                <p className='vial__d m-0 min-w-0 text-[length:var(--fs-sm)] leading-[1.58] text-[color:var(--fg-dim)]'>{t(`potions.vials.${vial.key}.description`)}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className='potions__foot mt-[clamp(1.5rem,_1rem_+_2vw,_2.5rem)] flex flex-wrap items-center justify-between gap-4 border-t border-solid border-t-[color:var(--rim)] pt-5' data-reveal=''>
          <p className='m-0 min-w-0 flex-[1_1_18rem] text-[length:var(--fs-md)] text-[color:var(--fg-dim)]'>
            <Trans t={t} i18nKey='potions.foot' components={{ b: <b className='text-[color:var(--fg-strong)]' /> }} />
          </p>
          <TrialCta className='btn btn-primary relative isolate inline-flex min-h-[var(--x-h)] min-w-0 items-center justify-center gap-2 whitespace-normal px-5 py-0 text-center text-[length:var(--fs-sm)] font-bold uppercase leading-[1.2] tracking-[.05em] no-underline [--x-h:3.25rem] min-[641px]:px-8 min-[641px]:py-0 min-[641px]:tracking-[var(--ls-caps)]' anchor={FINAL_FORM_ANCHOR}>
            {t('cta.try_free')}
          </TrialCta>
        </div>
      </div>
    </section>
  );
}
